import { useState } from "react";
import TableContainer from "../../../../components/TableContainer/TableContainer";

import Button from "../../../../components/Button/Button";
import styles from "./IncomingPayments.module.css";

import { ArrowDropDown, ArrowDropUp, Edit } from "@mui/icons-material";

import { useInfiniteQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getIncomingPayments } from "../../../../services/customers";
import type {
  ICostumersFinancialEntriesResponse,
  ICustomerOrdem,
} from "../../../../types/customers";

interface prop {
  uid_cliente: string;
}

type fieldOrder =
  | "tipo"
  | "numero_documento"
  | "date_initial"
  | "status"
  | "codigo_estabelecimento"
  | "descricao"
  | "conta_financeira"
  | "valor_parcela"
  | "parcela"
  | "data_vencimento"
  | "data_pagamento"
  | "uid_cliente"
  | "tipo_pagamento";

const FinancialEntriesList = ({ uid_cliente }: prop) => {
  const navigate = useNavigate();
  const [orderTable, setOrderTable] = useState<ICustomerOrdem>({
    codigo_estabelecimento: "DESC",
  });

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery<ICostumersFinancialEntriesResponse>({
      queryKey: ["getIncomingPayments", uid_cliente, orderTable],
      queryFn: ({ pageParam = 1 }) =>
        getIncomingPayments(uid_cliente, pageParam as number, orderTable),
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.hasMore) {
          return allPages.length + 1;
        }
        return undefined;
      },
      initialPageParam: 1,
      enabled: !!uid_cliente,
      refetchOnWindowFocus: false,
    });

  const dateForm = (dateString: string | Date | undefined) => {
    if (!dateString) return "---";
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR", {
      timeZone: "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const formatCurrency = (value: number | undefined) => {
    if (value === undefined || value === null) return "R$ 0,00";

    const numericValue = typeof value === "string" ? parseFloat(value) : value;

    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(numericValue);
  };

  const handleChangeOrder = (field: fieldOrder) => {
    setOrderTable({
      [field]: orderTable[field] === "ASC" ? "DESC" : "ASC",
    });
  };

  const renderSortableHeader = (field: fieldOrder, label: string) => {
    return (
      <Button
        variant="textButton"
        onClick={() => handleChangeOrder(field)}
        title={orderTable[field] === "DESC" ? "Decrescente" : "Crescente"}
      >
        {label}
        {orderTable[field] ? (
          orderTable[field] === "DESC" ? (
            <ArrowDropDown />
          ) : (
            <ArrowDropUp />
          )
        ) : (
          ""
        )}
      </Button>
    );
  };

  const statusMapFinancial = (
    status?: "aberto" | "pago" | "atrasado" | "cancelado" | "pagar" | "receber",
  ) => {
    switch (status) {
      case "aberto":
        return { text: "Em aberto", color: "blue-finance" };
      case "pago":
        return { text: "Pago", color: "green-finance" };
      case "atrasado":
        return { text: "Atrasado", color: "red-finance" };
      case "cancelado":
        return { text: "Cancelado", color: "gray-finance" };
      case "receber":
        return { text: "A Receber", color: "green-type" };
      case "pagar":
        return { text: "A Pagar", color: "yellow-type" };
      default:
        return { text: "N/A", color: "default" };
    }
  };

  return (
    <div className={styles.containerTable}>
      <TableContainer
        columns={[
          {
            key: "codEx",
            titulo: renderSortableHeader("codigo_estabelecimento", "Código"),
            classStyle: styles.columnCod,
          },
          {
            key: "descricaoEx",
            titulo: renderSortableHeader("descricao", "Descrição"),
            classStyle: styles.columnDescription,
          },
          {
            key: "numDocEx",
            titulo: renderSortableHeader("numero_documento", "Número doc"),
            classStyle: styles.columnNumDoc,
          },
          {
            key: "contaFinanceiraEx",
            titulo: renderSortableHeader(
              "conta_financeira",
              "Conta financeira",
            ),
            classStyle: styles.columnFinancialAccount,
          },
          {
            key: "valorEx",
            titulo: renderSortableHeader("valor_parcela", "Valor Parcela"),
            classStyle: styles.columnAmount,
          },
          {
            key: "tipoEx",
            titulo: renderSortableHeader("tipo_pagamento", "Tipo"),
            classStyle: styles.columnPaymentType,
          },
          {
            key: "parcelaEx",
            titulo: renderSortableHeader("parcela", "Parcela"),
            classStyle: styles.columnInstallment,
          },
          {
            key: "vencimentoEx",
            titulo: renderSortableHeader("data_vencimento", "Vencimento"),
            classStyle: styles.columnExpiration,
          },
          {
            key: "pagamentoEx",
            titulo: renderSortableHeader("data_pagamento", "Pagamento"),
            classStyle: styles.columnExpiration,
          },
          {
            key: "statusEx",
            titulo: renderSortableHeader("status", "Status"),
            classStyle: styles.columnStatus,
          },
          {
            key: "actionEx",
            titulo: "Ação",
            classStyle: styles.columnAction,
          },
        ]}
        content={
          data?.pages.flatMap(
            (page) =>
              page?.rows.map((item) => {
                const statusInfo = statusMapFinancial(item.status);

                return {
                  values: {
                    codEx: item.codigo_estabelecimento,

                    descricaoEx: item.descricao,

                    numDocEx: item.numero_documento || "-",

                    contaFinanceiraEx: item.conta_financeira?.nome || "-",
                    valorEx: formatCurrency(item.valor_parcela),

                    tipoEx: item.tipo_pagamento?.titulo || "-",

                    parcelaEx:
                      item.total_parcelas > 1
                        ? `${item.parcela} de ${item.total_parcelas}`
                        : "Única",
                    vencimentoEx: dateForm(item.data_vencimento),

                    pagamentoEx: (
                      <span
                        className={`${styles.statusBadge} ${styles[`status-${statusInfo.color}`]}`}
                      >
                        {dateForm(item.data_pagamento)}
                      </span>
                    ),
                    statusEx: (
                      <span
                        className={`${styles.statusBadge} ${styles[`status-${statusInfo.color}`]}`}
                      >
                        {statusInfo.text}
                      </span>
                    ),
                  },
                  buttons: [
                    {
                      variant: "contain",
                      typeStyle: "small",
                      type: "button",
                      title: "Conferir registro da conta",
                      onClick: () => {
                        navigate(
                          `edit/${item.uid_financeiro_lancamento}`,
                        );
                      },
                      icon: <Edit />,
                    },
                  ],
                };
              }) ?? [],
          ) ?? []
        }
      />
      {hasNextPage && (
        <div className={styles.loadMore}>
          <Button
            variant="contain"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            Carregar mais
          </Button>
        </div>
      )}
    </div>
  );
};

export default FinancialEntriesList;
